import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  suppliersApi,
  AddSupplierRequest,
} from '@texas/api/endpoints/suppliersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { reactEvents } from '@bridge/reactEvents';
import { RoleRestrict } from '@texas/components/shared/RoleRestrict';
import { Role } from '@texas/api/endpoints/userApi';

export function NewSupplier({
  buttonVariant = 'texas-solid',
}: {
  buttonVariant?: string;
} = {}) {
  const toast = useToast();
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const {
    request: createRequest,
    error,
    loading,
  } = useApiRequest(suppliersApi.createSupplier);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AddSupplierRequest>({
    defaultValues: { name: '', isInternal: false },
  });

  const onSubmit = async (data: AddSupplierRequest) =>
    await request(
      createRequest,
      [data],
      () => {
        toast({
          title: t('general.created'),
          status: 'success',
          isClosable: true,
        });
        reset();
        onClose();
        reactEvents.supplierCreated.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.createFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );

  return (
    <RoleRestrict allow={[Role.SystemAdministrator]}>
      <Button variant={buttonVariant} onClick={onOpen}>
        {t('suppliers.newSupplier')}
      </Button>
      <Modal
        onClose={() => {
          reset();
          onClose();
        }}
        isOpen={isOpen}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader fontWeight="bold">
            {t('suppliers.addSupplier')}
          </ModalHeader>
          <ModalBody>
            {error && <ErrorDetails error={error} />}
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
              <Flex direction="column" gap={6}>
                <FormControl isRequired={true} isInvalid={!!errors.name}>
                  <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                  <Input
                    variant="outline"
                    {...register('name', {
                      required: true,
                      maxLength: {
                        value: 100,
                        message: t('errors.maxLength', { count: 100 }),
                      },
                    })}
                    placeholder={t('general.name')}
                  />
                  <ErrorLabel text={errors.name?.message} />
                </FormControl>
                <FormControl>
                  <Checkbox {...register('isInternal')}>
                    {t('suppliers.isInternal')}
                  </Checkbox>
                  <Text variant="sub">{t('suppliers.internalDesc')}</Text>
                </FormControl>
                <SubmitButton loading={loading}>
                  {t('general.create')}
                </SubmitButton>
              </Flex>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </RoleRestrict>
  );
}
